import * as React from "react";
import {
  List, Datagrid, TextField, NumberField, Show, TabbedShowLayout,
  Edit, SimpleForm, NumberInput, SelectInput, useRecordContext,
} from "react-admin";
import { Box } from "@mui/material";
import { EnumChip } from "../../components/EnumChip";
import { JsonField } from "../../components/JsonField";
import { ConfirmActionButton } from "../../components/ConfirmActionButton";
import { FORFAIT_STATUS, choicesFromEnum } from "../../enums";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";
import { FrDateField } from "../../components/FrDateField";
import { UserRefField } from "../../components/ui";

// ForfaitController : GET /forfaits (liste admin, filtre `status` seul),
// PUT /forfaits/{id} (montant + volume uniquement), actions POST
// …/valider|refuser|payer|consommer|confirm-payment. Pas de POST /forfaits
// (les forfaits sont proposés par les pros : POST /forfaits/proposer) ni de
// GET /forfaits/{id} → pas de création, fiche lue depuis la liste.
const filters = [
  <SelectInput key="status" source="status" label="Statut" choices={choicesFromEnum(FORFAIT_STATUS)} alwaysOn />,
];

// ProposerForfaitRequest : seuls amount / totalSessions / totalMinutes /
// totalHours sont pris en compte par PUT /forfaits/{id}.
const Form = () => (
  <SimpleForm>
    <NumberInput source="amount" label="Montant (€)" />
    <NumberInput source="totalSessions" label="Nombre de séances" />
    <NumberInput source="totalMinutes" label="Minutes" />
    <NumberInput source="totalHours" label="Heures" />
  </SimpleForm>
);

const Validate = () => {
  const r = useRecordContext();
  if (!r || r.status === "PAYE") return null;
  return (
    <ConfirmActionButton label="Valider (post-paiement)" color="success"
      confirmTitle="Valider" confirmText="Marquer le forfait comme payé/validé ?"
      successMessage="Forfait validé"
      onConfirm={() => httpClient(`${API_BASE_URL}/forfaits/${r.id}/valider`, { method: "POST" })}
    />
  );
};
const Refuse = () => {
  const r = useRecordContext();
  if (!r || r.status === "ANNULE" || r.status === "PAYE") return null;
  return (
    <ConfirmActionButton label="Refuser la proposition" color="error"
      confirmTitle="Refuser" confirmText="Confirmer le refus ?"
      successMessage="Forfait refusé"
      onConfirm={() => httpClient(`${API_BASE_URL}/forfaits/${r.id}/refuser`, { method: "POST" })}
    />
  );
};
const InitPayment = () => {
  const r = useRecordContext();
  if (!r || r.status === "PAYE") return null;
  return (
    <ConfirmActionButton label="Initier le paiement Stripe" color="info"
      confirmTitle="Paiement" confirmText="Initier le PaymentIntent ?"
      successMessage="Paiement initié"
      onConfirm={() => httpClient(`${API_BASE_URL}/forfaits/${r.id}/payer`, { method: "POST" })}
    />
  );
};
const Consume = () => {
  const r = useRecordContext();
  if (!r || r.status !== "PAYE") return null;
  return (
    <ConfirmActionButton label="Consommer 1 séance" color="warning"
      confirmTitle="Consommer" confirmText="Décrémenter d'une séance ?"
      successMessage="1 séance consommée"
      onConfirm={() => httpClient(`${API_BASE_URL}/forfaits/${r.id}/consommer`, { method: "POST" })}
    />
  );
};
const ConfirmPayment = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <ConfirmActionButton label="Confirmer paiement (fallback)" color="info"
      confirmTitle="Confirmer" confirmText="Forcer la confirmation ?"
      successMessage="Paiement confirmé"
      onConfirm={() => httpClient(`${API_BASE_URL}/forfaits/${r.id}/confirm-payment`, { method: "POST" })}
    />
  );
};

const BookingsConsumed = () => {
  const r = useRecordContext();
  const [bookings, setBookings] = React.useState([]);
  React.useEffect(() => {
    if (!r?.id) return;
    httpClient(`${API_BASE_URL}/forfaits/${r.id}/bookings`)
      .then(({ json }) => setBookings(Array.isArray(json) ? json : json?.data || []))
      .catch(() => setBookings([]));
  }, [r?.id]);
  if (!bookings.length) return <Box sx={{ p: 1, color: "text.secondary" }}>Aucun rendez-vous consommé</Box>;
  return (
    <Box component="pre" sx={{
      bgcolor: "#f5f7fa", p: 1.5, fontSize: 12, maxHeight: 300, overflow: "auto",
    }}>
      {JSON.stringify(bookings, null, 2)}
    </Box>
  );
};

export default {
  list: () => (
    <List filters={filters} sort={{ field: "createdAt", order: "DESC" }}>
      <Datagrid rowClick="show">
        <UserRefField source="patientId" label="Patient" />
        <UserRefField source="professionalId" label="Professionnel" />
        <NumberField locales="fr-FR" source="totalSessions" label="Total" />
        <NumberField locales="fr-FR" source="usedSessions" label="Utilisées" />
        <NumberField source="amount" label="Montant" locales="fr-FR" options={{ style: "currency", currency: "EUR" }} />
        <EnumChip source="status" enumObj={FORFAIT_STATUS} />
        <FrDateField source="createdAt" label="Créé le" />
        <FrDateField source="expiresAt" label="Expire le" />
      </Datagrid>
    </List>
  ),
  show: () => (
    <Show>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Détails">
          <UserRefField source="patientId" label="Patient" />
          <UserRefField source="professionalId" label="Professionnel" />
          <NumberField locales="fr-FR" source="totalSessions" label="Séances au total" />
          <NumberField locales="fr-FR" source="usedSessions" label="Séances utilisées" />
          <NumberField source="amount" label="Montant" locales="fr-FR" options={{ style: "currency", currency: "EUR" }} />
          <EnumChip source="status" enumObj={FORFAIT_STATUS} />
          <FrDateField source="createdAt" label="Créé le" showTime />
          <FrDateField source="expiresAt" label="Expire le" />
          <TextField source="description" />
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Validate /><Refuse /><InitPayment /><Consume /><ConfirmPayment />
          </Box>
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="RDV consommés">
          <BookingsConsumed />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Brut">
          <JsonField source="__all__" />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  ),
  edit: () => <Edit mutationMode="pessimistic"><Form /></Edit>,
};
